'use client';

import { useTranslation } from 'react-i18next';

import { organizationPath } from '@kit/shared/handles';
import {
  EntityCard,
  EntityCardBookmark,
  EntityCardCaption,
  EntityCardCover,
  EntityCardKicker,
  EntityCardSub,
  EntityCardTitle,
} from '@kit/ui/entity-card';

import { BookmarkButton } from '~/lib/bookmarks/components/bookmark-button';
import { getCountryName } from '~/lib/constants/countries';

interface OrganizationListItem {
  id: string;
  handle: string | null;
  name: string;
  description: string | null;
  organization_type: string | null;
  organization_reach: string | null;
  verified: boolean;
  logo_url: string | null;
  header_url: string | null;
  dance_styles: string[] | null;
  country_code: string | null;
  city: string | null;
}

interface OrganizationCardProps {
  organization: OrganizationListItem;
  showCountry?: boolean;
  priority?: boolean;
}

// EN fallbacks for each organization type; the live label resolves through the
// `organizations:types.<value>` namespace at render time.
const TYPE_DEFAULT_LABELS: Record<string, string> = {
  school: 'School',
  company: 'Company',
  course: 'Course',
  competition: 'Competition',
  foundation: 'Foundation',
  brand: 'Brand',
  service: 'Service',
};

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function getTypeLabel(
  type: string | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!type) return null;
  const key = type.toLowerCase();
  const defaultLabel = TYPE_DEFAULT_LABELS[key];
  if (!defaultLabel) return type;
  return t(`organizations:types.${key}`, { defaultValue: defaultLabel });
}

export function OrganizationCard({
  organization,
  showCountry = true,
  priority = false,
}: OrganizationCardProps) {
  const { t } = useTranslation();

  if (!organization.handle) return null;

  const initials = getInitials(organization.name);
  const countryName = organization.country_code
    ? getCountryName(organization.country_code)
    : null;
  const typeLabel = getTypeLabel(organization.organization_type, t);

  const locationLabel = (() => {
    if (organization.city && countryName)
      return `${organization.city}, ${countryName}`;
    if (organization.city) return organization.city;
    if (showCountry && countryName) return countryName;
    return null;
  })();

  const stylesLabel = (() => {
    if (!organization.dance_styles || organization.dance_styles.length === 0) {
      return null;
    }
    return organization.dance_styles
      .slice(0, 3)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' · ');
  })();

  const kickerLabel = [typeLabel, stylesLabel].filter(Boolean).join('  ·  ');

  return (
    <EntityCard
      href={organizationPath(organization.handle)}
      aspect="3/1"
      ariaLabel={organization.name}
      fallbackSeed={organization.id}
      testId="organization-card"
    >
      <EntityCardBookmark className="top-3 right-3">
        <BookmarkButton
          bookmarkableType="organization"
          bookmarkableId={organization.id}
          variant="icon"
          size="sm"
          className="bg-background/80 hover:bg-background size-8 border-0 backdrop-blur-md"
        />
      </EntityCardBookmark>

      <EntityCardCover
        src={organization.header_url}
        alt={organization.name}
        fallbackInitials={initials}
        priority={priority}
        coinMark={{
          src: organization.logo_url,
          alt: organization.name,
          fallbackInitials: initials,
          priority,
        }}
      />

      <EntityCardCaption>
        <EntityCardTitle
          verified={organization.verified}
          verifiedLabel={t('community:verified')}
        >
          {organization.name}
        </EntityCardTitle>

        {locationLabel && <EntityCardSub>{locationLabel}</EntityCardSub>}

        {kickerLabel && <EntityCardKicker>{kickerLabel}</EntityCardKicker>}
      </EntityCardCaption>
    </EntityCard>
  );
}
